import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import {
  FileText,
  DollarSign,
  Target,
  Calculator,
  Plus,
  Loader,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  Building2,
  TrendingUp,
  Calendar,
  Network,
  MapPin
} from 'lucide-react';
import axiosInstance from '../api/axios';
import { MATERIALITY_ASSESSMENT_STORAGE_KEY } from '../utils/materialityAssessmentState';
import { getSoxCycles, availableSoxCycles, resolveIncomeStatementMappingByItemId } from '../utils/soxCycleReference';
import useAuthUser from '../hooks/useAuthUser';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import ModuleAuditDownloadButton from './shared/ModuleAuditDownloadButton.tsx';

function MaterialityDashboard() {
  const { selectedAuditYearId, selectedAuditYear } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [latestMateriality, setLatestMateriality] = useState(null);
  const [riskCounts, setRiskCounts] = useState({ high: 0, medium: 0, low: 0 });
  const [isAnimated, setIsAnimated] = useState(false);
  const [uniqueSoxCycles, setUniqueSoxCycles] = useState(0);
  const [entitiesInScope, setEntitiesInScope] = useState(0);
  const [mostRepeatedSoxCycle, setMostRepeatedSoxCycle] = useState(null);
  const authenticatedUser = useAuthUser();

  useEffect(() => {
    fetchAssessments();
    // Trigger fade-in animation on mount
    const timer = setTimeout(() => setIsAnimated(true), 100);
    return () => clearTimeout(timer);
  }, [selectedAuditYearId]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateRiskCounts = (assessment) => {
    let high = 0;
    let medium = 0;
    let low = 0;

    if (!assessment.sox_cycle_json) {
      return { high, medium, low };
    }

    // Get line item data from riskScores or lineItemData
    let rawData = assessment.sox_cycle_json.riskScores ||
      assessment.sox_cycle_json.lineItemData ||
      assessment.sox_cycle_json.allLineItemData;

    // Handle array format (allLineItemData)
    if (Array.isArray(rawData) && rawData.length > 0) {
      rawData = rawData[0].lineItems;
    }

    if (!rawData) {
      return { high, medium, low };
    }

    // Count balance sheet items
    if (rawData.balance_sheet && rawData.balance_sheet.items) {
      rawData.balance_sheet.items.forEach(item => {
        const riskScore = item.risk_score || item.riskScore;
        if (riskScore) {
          const score = parseFloat(riskScore);
          if (score <= 3.3) {
            low++;
          } else if (score <= 6.6) {
            medium++;
          } else {
            high++;
          }
        }
      });
    }

    // Count income statement items
    if (rawData.income_statement && rawData.income_statement.items) {
      rawData.income_statement.items.forEach(item => {
        const riskScore = item.risk_score || item.riskScore;
        if (riskScore) {
          const score = parseFloat(riskScore);
          if (score <= 3.3) {
            low++;
          } else if (score <= 6.6) {
            medium++;
          } else {
            high++;
          }
        }
      });
    }

    return { high, medium, low };
  };

  const calculateUniqueSoxCycles = (assessment) => {
    const allSoxCycles = new Set();

    if (!assessment.sox_cycle_json) return 0;

    let rawData = assessment.sox_cycle_json.riskScores ||
      assessment.sox_cycle_json.lineItemData ||
      assessment.sox_cycle_json.allLineItemData;

    if (Array.isArray(rawData) && rawData.length > 0) {
      rawData = rawData[0].lineItems;
    }

    if (!rawData) return 0;

    const customSoxMappings = assessment.sox_cycle_json.customSoxMappings || {};

    // Collect from balance sheet
    if (rawData.balance_sheet && rawData.balance_sheet.items) {
      rawData.balance_sheet.items.forEach((item, idx) => {
        // Determine itemType based on item.category
        const itemType = item.category === 'asset' ? 'bs_asset' :
          item.category === 'liability' ? 'bs_liability' : 'bs_equity';
        // Build itemId in the same format as used in Materiality.js
        const itemId = `${itemType}_${idx}_${(item.label || '').replace(/\s+/g, '_').toLowerCase()}`;
        const soxCycles =
          item.sox_cycles || customSoxMappings[itemId] || getSoxCycles(item.label);
        soxCycles.forEach(cycle => allSoxCycles.add(cycle));
      });
    }

    // Collect from income statement
    if (rawData.income_statement && rawData.income_statement.items) {
      rawData.income_statement.items.forEach((item, idx) => {
        const itemType = item.category === 'revenue' ? 'is_revenue' : 'is_expense';
        // Build itemId in the same format as used in Materiality.js
        const itemId = `${itemType}_${idx}_${(item.label || '').replace(/\s+/g, '_').toLowerCase()}`;
        const customMapped = resolveIncomeStatementMappingByItemId(customSoxMappings, itemId);
        const soxCycles = item.sox_cycles || customMapped || getSoxCycles(item.label);
        soxCycles.forEach(cycle => allSoxCycles.add(cycle));
      });
    }

    return allSoxCycles.size;
  };

  const calculateEntitiesInScope = (assessment) => {
    if (!assessment.entityscoping_json || !assessment.entityscoping_json.entityScopingData) {
      return 0;
    }

    const entityScopingData = assessment.entityscoping_json.entityScopingData;
    const entityConclusions = assessment.entityscoping_json.entityConclusions || {};
    let count = 0;

    // Count entities where quantitative conclusion is "In-Scope"
    entityScopingData.forEach(entity => {
      const quantitativeConclusion = entityConclusions[entity.entity] || entity.conclusion || '';
      if (quantitativeConclusion === 'In-Scope') {
        count++;
      }
    });

    return count;
  };

  const calculateMostRepeatedSoxCycle = (assessment) => {
    const cycleCounts = {};

    if (!assessment.sox_cycle_json) return null;

    let rawData = assessment.sox_cycle_json.riskScores ||
      assessment.sox_cycle_json.lineItemData ||
      assessment.sox_cycle_json.allLineItemData;

    if (Array.isArray(rawData) && rawData.length > 0) {
      rawData = rawData[0].lineItems;
    }

    if (!rawData) return null;

    const customSoxMappings = assessment.sox_cycle_json.customSoxMappings || {};

    // Count from balance sheet
    if (rawData.balance_sheet && rawData.balance_sheet.items) {
      rawData.balance_sheet.items.forEach((item, idx) => {
        // Determine itemType based on item.category
        const itemType = item.category === 'asset' ? 'bs_asset' :
                        item.category === 'liability' ? 'bs_liability' : 'bs_equity';
        // Build itemId in the same format as used in Materiality.js
        const itemId = `${itemType}_${idx}_${(item.label || '').replace(/\s+/g, '_').toLowerCase()}`;
        const soxCycles = item.sox_cycles || customSoxMappings[itemId] || getSoxCycles(item.label);
        soxCycles.forEach(cycle => {
          cycleCounts[cycle] = (cycleCounts[cycle] || 0) + 1;
        });
      });
    }

    // Count from income statement
    if (rawData.income_statement && rawData.income_statement.items) {
      rawData.income_statement.items.forEach((item, idx) => {
        const itemType = item.category === 'revenue' ? 'is_revenue' : 'is_expense';
        // Build itemId in the same format as used in Materiality.js
        const itemId = `${itemType}_${idx}_${(item.label || '').replace(/\s+/g, '_').toLowerCase()}`;
        const customMapped = resolveIncomeStatementMappingByItemId(customSoxMappings, itemId);
        const soxCycles = item.sox_cycles || customMapped || getSoxCycles(item.label);
        soxCycles.forEach(cycle => {
          cycleCounts[cycle] = (cycleCounts[cycle] || 0) + 1;
        });
      });
    }

    // Find the most repeated cycle(s) - handle ties
    let maxCount = 0;
    let mostRepeatedCycles = [];

    Object.entries(cycleCounts).forEach(([cycle, count]) => {
      if (count > maxCount) {
        maxCount = count;
        mostRepeatedCycles = [cycle];
      } else if (count === maxCount) {
        mostRepeatedCycles.push(cycle);
      }
    });

    return mostRepeatedCycles.length > 0 ? { codes: mostRepeatedCycles, count: maxCount } : null;
  };
  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const hasUser = authenticatedUser && (authenticatedUser.id != null || authenticatedUser.username);
      if (!hasUser) {
        setError('No user session found');
        setLoading(false);
        return;
      }
      const matParams = selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {};
      const response = await axiosInstance.get('/api/materiality-assessments', { params: matParams });

      if (response.data.success && response.data.assessments) {
        setAssessments(response.data.assessments);
        if (response.data.assessments.length > 0) {
          const latest = response.data.assessments[0];
          setLatestMateriality(latest.materiality_json?.confirmedMateriality);

          // Calculate risk counts
          const counts = calculateRiskCounts(latest);
          setRiskCounts(counts);

          // Calculate SOX cycle metrics
          const soxCyclesCount = calculateUniqueSoxCycles(latest);
          setUniqueSoxCycles(soxCyclesCount);

          // Calculate entities in scope
          const entitiesCount = calculateEntitiesInScope(latest);
          setEntitiesInScope(entitiesCount);

          // Calculate most repeated SOX cycle
          const mostRepeated = calculateMostRepeatedSoxCycle(latest);
          setMostRepeatedSoxCycle(mostRepeated);
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching assessments:', err);
      setError('Failed to load assessments');
      setLoading(false);
    }
  };

  const handleNewRiskAssessment = () => {
    // Clear all saved state from localStorage to start fresh
    localStorage.removeItem(MATERIALITY_ASSESSMENT_STORAGE_KEY);
    localStorage.removeItem('assessmentVersionName');
    localStorage.removeItem('soxAssessmentData');
    localStorage.removeItem('lastAssessmentId');
    localStorage.removeItem('lastAssessmentName');

    // Navigate to materiality page - it will start from step 0/1
    navigate('/materiality');
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return `$${parseInt(value).toLocaleString()}`;
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return past.toLocaleDateString();
  };

  const generateActivityLog = () => {
    const activities = [];

    assessments.forEach((assessment, index) => {
      const createdAt = assessment.created_at;
      const materialityData = assessment.materiality_json;
      const soxData = assessment.sox_cycle_json;

      // Assessment created activity
      activities.push({
        type: 'assessment_created',
        title: `Risk Assessment Completed: ${assessment.analysis_name}`,
        description: `New materiality assessment created with ${riskCounts.high + riskCounts.medium + riskCounts.low} line items analyzed`,
        timestamp: createdAt,
        icon: CheckCircle,
        iconColor: 'text-green-600',
        bgColor: 'bg-green-50'
      });

      // Company profile activity
      if (materialityData?.companyProfile) {
        activities.push({
          type: 'company_info',
          title: `Company Profile: ${materialityData.companyProfile.companyName}`,
          description: `${materialityData.companyProfile.industry} • ${materialityData.companyProfile.growthStage}`,
          timestamp: createdAt,
          icon: Building2,
          iconColor: 'text-blue-600',
          bgColor: 'bg-blue-50'
        });
      }

      // Materiality calculation activity
      if (materialityData?.confirmedMateriality && index === 0) {
        activities.push({
          type: 'materiality_updated',
          title: 'Materiality Thresholds Updated',
          description: `Overall: ${formatCurrency(materialityData.confirmedMateriality.overallMateriality)} • Planning: ${formatCurrency(materialityData.confirmedMateriality.planningMateriality)}`,
          timestamp: createdAt,
          icon: TrendingUp,
          iconColor: 'text-indigo-600',
          bgColor: 'bg-indigo-50'
        });
      }

      // High risk items identified
      if (index === 0 && riskCounts.high > 0) {
        activities.push({
          type: 'high_risk',
          title: `${riskCounts.high} High Risk Items Identified`,
          description: 'Immediate attention required for high-risk line items',
          timestamp: createdAt,
          icon: AlertTriangle,
          iconColor: 'text-red-600',
          bgColor: 'bg-red-50'
        });
      }
    });

    // Sort by timestamp descending
    return activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 8);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Risk and Scoping Dashboard</h1>
                <p className="text-sm text-gray-600 mt-1">Overview of materiality analysis and risk assessment results</p>
              </div>
              <div className="flex items-center gap-2">
                <ModuleAuditDownloadButton
                  resourceType="materiality_assessment"
                  moduleKey={MODULE_KEYS.MATERIALITY}
                  filenamePrefix="materiality_audit"
                />
                <button
                  onClick={handleNewRiskAssessment}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  New Risk Assessment
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex flex-col items-center justify-center">
            <Loader className="h-12 w-12 text-blue-600 animate-spin mb-4" />
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!latestMateriality || assessments.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Risk and Scoping Dashboard</h1>
                <p className="text-sm text-gray-600 mt-1">Overview of materiality analysis and risk assessment results</p>
              </div>
              <div className="flex items-center gap-2">
                <ModuleAuditDownloadButton
                  resourceType="materiality_assessment"
                  moduleKey={MODULE_KEYS.MATERIALITY}
                  filenamePrefix="materiality_audit"
                />
                <button
                  onClick={handleNewRiskAssessment}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  New Risk Assessment
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Risk Assessments Yet</h2>
            <p className="text-gray-600 mb-6 max-w-md">
              Get started by creating your first risk assessment to see materiality calculations and dashboard insights.
            </p>
            <button
              onClick={handleNewRiskAssessment}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Create Risk Assessment
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 transition-opacity duration-500 ${isAnimated ? 'opacity-100' : 'opacity-0'}`}>
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Risk and Scoping Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">Overview of materiality analysis and risk assessment results</p>
            </div>
            <div className="flex items-center gap-2">
              <ModuleAuditDownloadButton
                resourceType="materiality_assessment"
                moduleKey={MODULE_KEYS.MATERIALITY}
                filenamePrefix="materiality_audit"
              />
              <button
                onClick={handleNewRiskAssessment}
                disabled={isLocked}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-4 w-4" />
                New Risk Assessment
              </button>
            </div>
          </div>
          <ReadOnlyBanner />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 transform transition-all duration-700"
          style={{ transform: isAnimated ? 'translateY(0)' : 'translateY(20px)', opacity: isAnimated ? 1 : 0 }}>
          {/* Overall Materiality */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-90 mb-1">Overall Materiality</p>
                <p className="text-3xl font-bold mb-2">{formatCurrency(latestMateriality?.overallMateriality)}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6" />
              </div>
            </div>
          </div>

          {/* Planning Materiality */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-90 mb-1">Planning Materiality</p>
                <p className="text-3xl font-bold mb-2">{formatCurrency(latestMateriality?.planningMateriality)}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Target className="h-6 w-6" />
              </div>
            </div>
          </div>

          {/* Sum-de-minimus */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-90 mb-1">Sum-de-minimus</p>
                <p className="text-3xl font-bold mb-2">{formatCurrency(latestMateriality?.deminimis)}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Calculator className="h-6 w-6" />
              </div>
            </div>
          </div>
        </div>

        {/* Risk Count Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 transform transition-all duration-700"
          style={{ transform: isAnimated ? 'translateY(0)' : 'translateY(20px)', opacity: isAnimated ? 1 : 0, transitionDelay: '150ms' }}>
          {/* High Risk Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">High Risk Items</p>
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <p className="text-3xl font-bold text-red-600">{riskCounts.high}</p>
            <p className="text-xs text-gray-500 mt-1">Risk Score &gt; 6.6</p>
          </div>

          {/* Medium Risk Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Medium Risk Items</p>
              <AlertCircle className="h-5 w-5 text-yellow-500" />
            </div>
            <p className="text-3xl font-bold text-yellow-600">{riskCounts.medium}</p>
            <p className="text-xs text-gray-500 mt-1">Risk Score 3.3 - 6.6</p>
          </div>

          {/* Low Risk Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Low Risk Items</p>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-green-600">{riskCounts.low}</p>
            <p className="text-xs text-gray-500 mt-1">Risk Score &lt;= 3.3</p>
          </div>
        </div>

        {/* SOX Cycle and Entity Scoping Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 transform transition-all duration-700"
          style={{ transform: isAnimated ? 'translateY(0)' : 'translateY(20px)', opacity: isAnimated ? 1 : 0, transitionDelay: '200ms' }}>
          {/* Unique SOX Cycles Mapped */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Unique SOX Cycles</p>
              <Network className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-blue-600">{uniqueSoxCycles}</p>
            <p className="text-xs text-gray-500 mt-1">Business cycles mapped</p>
          </div>

          {/* Entities in Scope */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Entities in Scope</p>
              <MapPin className="h-5 w-5 text-purple-500" />
            </div>
            <p className="text-3xl font-bold text-purple-600">{entitiesInScope}</p>
            <p className="text-xs text-gray-500 mt-1">In-scope entities</p>
          </div>

          {/* Most Repeated SOX Cycle */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Most Repeated Cycle{mostRepeatedSoxCycle?.codes?.length > 1 ? 's' : ''}</p>
              <TrendingUp className="h-5 w-5 text-indigo-500" />
            </div>
            {mostRepeatedSoxCycle ? (
              <div className="relative group">
                <div className="flex items-baseline gap-2 flex-wrap">
                  {mostRepeatedSoxCycle.codes.map((code, idx) => (
                    <div key={code} className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-indigo-600 cursor-help">
                        {code}
                      </span>
                      {idx < mostRepeatedSoxCycle.codes.length - 1 && (
                        <span className="text-2xl font-bold text-gray-400">,</span>
                      )}
                    </div>
                  ))}
                  <span className="text-sm text-gray-500">
                    ({mostRepeatedSoxCycle.count}x)
                  </span>
                </div>
                <div className="invisible group-hover:visible absolute left-0 top-full mt-2 px-3 py-2 bg-gray-900 text-white text-xs rounded max-w-xs z-10">
                  {mostRepeatedSoxCycle.codes.map((code, idx) => (
                    <div key={code}>
                      {availableSoxCycles.find(c => c.code === code)?.name || code}
                      {idx < mostRepeatedSoxCycle.codes.length - 1 && ', '}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-3xl font-bold text-gray-400">—</p>
            )}
            <p className="text-xs text-gray-500 mt-1">Most frequently mapped</p>
          </div>
        </div>

        {/* Activity Log Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Recent Activity */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow transform transition-all duration-700"
            style={{ transform: isAnimated ? 'translateY(0)' : 'translateY(20px)', opacity: isAnimated ? 1 : 0, transitionDelay: '300ms' }}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                </div>
                <span className="text-sm text-gray-500">{assessments.length} assessment{assessments.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {generateActivityLog().map((activity, index) => {
                  const Icon = activity.icon;
                  return (
                    <div key={index} className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className={`flex-shrink-0 w-10 h-10 ${activity.bgColor} rounded-lg flex items-center justify-center`}>
                        <Icon className={`h-5 w-5 ${activity.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 mb-1">{activity.title}</p>
                        <p className="text-sm text-gray-600 mb-1">{activity.description}</p>
                        <p className="text-xs text-gray-500">{getTimeAgo(activity.timestamp)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div className="bg-white rounded-lg shadow transform transition-all duration-700"
            style={{ transform: isAnimated ? 'translateY(0)' : 'translateY(20px)', opacity: isAnimated ? 1 : 0, transitionDelay: '350ms' }}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Key Insights</h2>
            </div>
            <div className="p-6 space-y-4">
              {/* Total Assessments */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  <p className="text-sm font-semibold text-blue-900">Total Assessments</p>
                </div>
                <p className="text-2xl font-bold text-blue-600">{assessments.length}</p>
                <p className="text-xs text-blue-700 mt-1">Risk assessments completed</p>
              </div>

              {/* Latest Company */}
              {assessments[0]?.materiality_json?.companyProfile && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="h-5 w-5 text-gray-600" />
                    <p className="text-sm font-semibold text-gray-900">Latest Company</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {assessments[0].materiality_json.companyProfile.companyName}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {assessments[0].materiality_json.companyProfile.industry}
                  </p>
                </div>
              )}

              {/* Risk Summary */}
              <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                  <p className="text-sm font-semibold text-purple-900">Risk Summary</p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">High Risk</span>
                    <span className="text-xs font-semibold text-red-600">{riskCounts.high}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Medium Risk</span>
                    <span className="text-xs font-semibold text-yellow-600">{riskCounts.medium}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Low Risk</span>
                    <span className="text-xs font-semibold text-green-600">{riskCounts.low}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaterialityDashboard;